/**
 * Post Recommend Render
 * - DOM operations only
 * - Modal HTML is injected via innerHTML (V2 convention)
 */

import { Roles } from "../../constants/roles";
import { Actions } from "../../constants/actions";
import { ModalSearchBase } from "../../components/modal-search/modal-search-base-handler";
import { ModalSearchListener } from "../../components/modal-search/modal-search-listener";
import { translator } from "../../translation/translator";
import commonUtil from "../../util/common-utils";
import { SpinnerRender } from "../../util/spinner/spinner-render";
import { RecommendBadgeIconsRender } from "../../recommend/recommend-badge-icons-render";
import { RecommendBadgeIconsUtils } from "../../recommend/recommend-badge-icons-utils";
import { postRecommendForm } from "./post-recommend-form";

interface RankButtonViewModel {
    rank: number;
    disabled: boolean;
    isCurrent: boolean;
}

interface RecommendViewModel {
    buttons: RankButtonViewModel[];
    canUnset: boolean;
}

interface RecommendActionCallbacks {
    onPickRank?: ((rank: number) => Promise<void> | void) | null;
    onUnset?: (() => Promise<void> | void) | null;
    onClearAll?: (() => Promise<void> | void) | null;
}

type ModalSearchBaseLike = ModalSearchBase;

class PostRecommendRender {
    public base: ModalSearchBaseLike | null;
    public viewModel: RecommendViewModel | null;
    public onPickRank: ((rank: number) => Promise<void> | void) | null;
    public onUnset: (() => Promise<void> | void) | null;
    public onClearAll: (() => Promise<void> | void) | null;

    public constructor() {
        this.base = null;
        this.viewModel = null;
        this.onPickRank = null;
        this.onUnset = null;
        this.onClearAll = null;
    }

    public getOverlay(): HTMLElement | null {
        return postRecommendForm.getOverlay();
    }

    public getModal(): HTMLElement | null {
        return postRecommendForm.getModal();
    }

    public getListRoot(): HTMLElement | null {
        return postRecommendForm.getListRoot(this.getModal() || document);
    }

    public renderLoading(): void {
        this.ensureOverlay();
        this.viewModel = null;

        if (this.base) {
            this.base.setSelectedIds([]);
            this.base.render();
        }
    }

    public setActions(actions: RecommendActionCallbacks): void {
        this.onPickRank = typeof actions?.onPickRank === "function" ? actions.onPickRank : null;
        this.onUnset = typeof actions?.onUnset === "function" ? actions.onUnset : null;
        this.onClearAll = typeof actions?.onClearAll === "function" ? actions.onClearAll : null;
    }

    public ensureOverlay(): void {
        if (this.getOverlay()) return;

        const t = (key: string, fallback: string): string => {
            const value = translator?.getTranslation(key);
            return value && value !== key ? value : fallback;
        };

        const html = `
            <div data-role="${Roles.POST_RECOMMEND_MODAL_OVERLAY}" class="post-recommend-modal-overlay" style="display:none">
                <div data-role="${Roles.POST_RECOMMEND_MODAL}" class="post-recommend-modal" style="display:none">
                    <div class="modal-header element-modal-header">
                        <div class="section-title-sub modal-title">
                            <span data-role="${Roles.POST_RECOMMEND_MODAL_TITLE}">
                                <span data-translate="post_recommend_rank_title">${commonUtil.escapeHtml(
                                    t("post_recommend_rank_title", "Recommended Rank"),
                                )}</span><span data-role="${Roles.POST_RECOMMEND_MODAL_BOARD_ID}"></span>
                            </span>
                        </div>
                        <button type="button" class="btn circle-clear modal-window element-modal-close" data-role="${Roles.POST_RECOMMEND_MODAL_CLOSE}" aria-label="${commonUtil.escapeHtmlAttribute(
                            t("post_recommend_close", "Close"),
                        )}">&times;</button>
                    </div>

                    <div class="post-recommend-modal__body">
                            <div class="post-recommend-modal__list" data-role="${Roles.POST_RECOMMEND_MODAL_LIST}" aria-live="polite"></div>
                    </div>

                    <div class="modal-footer element-modal-footer post-recommend-modal__footer">
                        <button type="button" class="btn delete" data-action="${Actions.SELECT_CLEAR_ALL}" data-translate="remove_all">${commonUtil.escapeHtml(
                            t("remove_all", "Remove All"),
                        )}</button>
                        <button type="button" class="btn clear" data-action="${Actions.SELECT_CLEAR}" data-translate="remove" disabled>${commonUtil.escapeHtml(
                            t("remove", "Remove"),
                        )}</button>
                    </div>
                </div>
            </div>
        `;

        document.body.insertAdjacentHTML("beforeend", html);

        try {
            translator?.updateContent?.();
        } catch (_error) {
            void _error;
        }

        const overlayEl = this.getOverlay();
        const modalEl = this.getModal();
        const listRoot = this.getListRoot();
        const closeBtnEl = postRecommendForm.getCloseButton();

        if (!overlayEl || !modalEl || !listRoot) return;

        this.base = new ModalSearchBase({
            overlayEl,
            modalEl,
            filterInputEl: null,
            adapter: {
                render: (base: ModalSearchBaseLike) => {
                    this.renderList(base);
                },
                handleAction: (base: ModalSearchBaseLike, action: string, actionEl: HTMLElement, event: Event) => {
                    if (action === Actions.SELECT_RANK) {
                        event.preventDefault();
                        const btn = actionEl instanceof HTMLButtonElement ? actionEl : null;
                        if (btn?.disabled) return true;

                        const rankRaw = String(actionEl.getAttribute("data-rank") || "").trim();
                        if (!rankRaw) return true;

                        if (base.hasSelectedId(rankRaw)) {
                            return true;
                        }

                        base.toggleSelectedId(rankRaw);
                        base.render();

                        const n = Number(rankRaw);
                        if (Number.isFinite(n) && n > 0) {
                            void this.onPickRank?.(Math.trunc(n));
                        }
                        return true;
                    }

                    if (action === Actions.SELECT_CLEAR) {
                        event.preventDefault();
                        const btn = actionEl instanceof HTMLButtonElement ? actionEl : null;
                        if (btn?.disabled) return true;
                        void this.onUnset?.();
                        return true;
                    }

                    if (action === Actions.SELECT_CLEAR_ALL) {
                        event.preventDefault();
                        const btn = actionEl instanceof HTMLButtonElement ? actionEl : null;
                        if (btn?.disabled) return true;
                        void this.onClearAll?.();
                        return true;
                    }

                    return false;
                },
            },
        });

        this.base.setSelectionMode("single");
        this.base.setCloseOnSelect(false);

        ModalSearchListener.setup({
            rootEl: document,
            scopeKey: "post-recommend-modal",
            overlayEl,
            modalEl,
            closeBtnEl: closeBtnEl instanceof HTMLElement ? closeBtnEl : null,
            filterInputEl: null,
            actionRootEls: [listRoot],
            base: this.base,
        });
    }

    public renderBody(viewModel: RecommendViewModel): void {
        this.ensureOverlay();
        this.viewModel = viewModel;

        if (this.base) {
            const current = (Array.isArray(viewModel?.buttons) ? viewModel.buttons : []).find((b) => !!b?.isCurrent);
            const currentRank = current ? String(current.rank) : "";
            this.base.setSelectedIds(currentRank ? [currentRank] : []);
            this.base.render();
        }
    }

    public renderList(base: ModalSearchBaseLike): void {
        const listRoot = this.getListRoot();
        if (!listRoot) return;

        if (!this.viewModel) {
            const spinner = SpinnerRender.createInlineSpinner({ size: "normal" });
            listRoot.innerHTML = `
                <div class="post-recommend-modal__loading" aria-label="loading">
                    ${spinner.outerHTML}
                </div>
            `;
            return;
        }

        const buttons = Array.isArray(this.viewModel?.buttons) ? this.viewModel.buttons : [];
        const canUnset = !!this.viewModel?.canUnset;
        const recommendCaption = RecommendBadgeIconsUtils.getRecommendBadgeCaption("recommend", "Recommend");

        listRoot.innerHTML = buttons
            .map((b) => {
                const r = Math.trunc(Number(b.rank));
                if (!Number.isFinite(r) || r <= 0) return "";

                const isSelected = base.hasSelectedId(String(r));
                const selectedClass = isSelected ? " post-recommend-rank-option--selected" : "";
                const ariaPressed = isSelected ? "true" : "false";
                const disabled = !!b.disabled;
                const disabledAttr = disabled ? "disabled" : "";

                return `
                    <button type="button" class="post-recommend-rank-option${selectedClass}" data-action="${Actions.SELECT_RANK}" aria-label="${commonUtil.escapeHtmlAttribute(
                        `${recommendCaption} ${String(r)}`,
                    )}" data-rank="${commonUtil.escapeHtmlAttribute(String(r))}" aria-pressed="${ariaPressed}" ${disabledAttr}>
                        <span class="product-recommend-rank-option__icon is-recommended" data-recommend-rank="${commonUtil.escapeHtmlAttribute(
                            String(r),
                        )}" aria-hidden="true">
                            ${RecommendBadgeIconsRender.recommendBadgeIconsTemplate}
                            <span class="recommend-badge__rank">${commonUtil.escapeHtml(String(r))}</span>
                        </span>
                    </button>
                `;
            })
            .filter(Boolean)
            .join("");

        const modal = this.getModal();
        if (modal) {
            const removeBtn = postRecommendForm.getUnsetButton(modal);
            if (removeBtn instanceof HTMLButtonElement) {
                removeBtn.disabled = !canUnset;
            }
        }
    }

    public open(): void {
        this.ensureOverlay();
        this.base?.open();
    }

    public setModalTitle(boardId: string | null | undefined): void {
        this.ensureOverlay();
        const titleRoot = postRecommendForm.getTitleRoot();
        if (!(titleRoot instanceof HTMLElement)) return;

        const suffixEl = postRecommendForm.getBoardIdSuffix(titleRoot);
        if (!(suffixEl instanceof HTMLElement)) return;

        const normalizedBoardId = String(boardId || "").trim();
        if (!normalizedBoardId) {
            suffixEl.textContent = "";
            return;
        }

        const boardLabelKey = `board_${normalizedBoardId}`;
        const boardLabelTranslated = translator?.getTranslation?.(boardLabelKey);
        const boardLabel =
            boardLabelTranslated && boardLabelTranslated !== boardLabelKey ? boardLabelTranslated : normalizedBoardId;

        suffixEl.textContent = ` (${boardLabel})`;
    }

    public close(): void {
        this.base?.close();
    }

    public setCrownState(crownEl: HTMLElement, recommended: boolean): void {
        crownEl.dataset.isRecommended = recommended ? "1" : "0";
    }

    public setCrownRank(crownEl: HTMLElement, rank: number | null): void {
        const existing = postRecommendForm.getRecommendBadgeRank(crownEl);
        const showRank = RecommendBadgeIconsUtils.shouldShowRecommendBadgeRank(crownEl);

        if (rank === null || typeof rank === "undefined") {
            delete crownEl.dataset.recommendRank;
            if (existing instanceof HTMLElement) {
                existing.remove();
            }
            return;
        }

        const n = Number(rank);
        if (!Number.isFinite(n) || n <= 0) {
            delete crownEl.dataset.recommendRank;
            if (existing instanceof HTMLElement) {
                existing.remove();
            }
            return;
        }

        crownEl.dataset.recommendRank = String(Math.trunc(n));

        if (!showRank) {
            if (existing instanceof HTMLElement) {
                existing.remove();
            }
            return;
        }

        if (existing instanceof HTMLElement) {
            existing.textContent = String(Math.trunc(n));
            return;
        }

        const el = document.createElement("span");
        el.className = "recommend-badge__rank";
        el.setAttribute("data-role", Roles.POST_RECOMMEND_BADGE_RANK);
        el.setAttribute("aria-hidden", "true");
        el.textContent = String(Math.trunc(n));
        crownEl.appendChild(el);
    }

    public setCrownLoading(crownEl: HTMLElement, loading: boolean): void {
        const existing = postRecommendForm.getRecommendBadgeSpinner(crownEl);

        if (!loading) {
            crownEl.classList.remove("loading");
            delete crownEl.dataset.loading;
            crownEl.removeAttribute("aria-busy");
            if (existing instanceof HTMLElement) {
                existing.remove();
            }
            return;
        }

        crownEl.classList.add("loading");
        crownEl.dataset.loading = "1";
        crownEl.setAttribute("aria-busy", "true");
        if (existing instanceof HTMLElement) {
            return;
        }

        const wrap = document.createElement("span");
        wrap.className = "recommend-badge__spinner";
        wrap.setAttribute("data-role", Roles.POST_RECOMMEND_BADGE_SPINNER);
        const spin = SpinnerRender.createInlineSpinner({ size: "small" });
        wrap.appendChild(spin);
        crownEl.appendChild(wrap);
    }

    public setBusy(busy: boolean): void {
        const modal = this.getModal();
        if (!modal) return;

        const btns = postRecommendForm.getModalButtons(modal);
        btns.forEach((b) => {
            if (busy) {
                if (!b.disabled) {
                    b.dataset.busyDisabled = "1";
                    b.disabled = true;
                }
                return;
            }

            if (b.dataset.busyDisabled === "1") {
                delete b.dataset.busyDisabled;
                b.disabled = false;
            }
        });
    }
}

export { PostRecommendRender };
